import type {
  DsarRequestPayload,
  LoginResponse,
  LoginVariables,
  PasswordRequirementsResponse,
  RegisterCdpVariables,
  RegisterVariables,
  RegisterVariablesError,
  RegisterVariablesSuccess,
  ResetPasswordResponse,
  VerifyUpdateEmailResponse,
} from "@jti/fe-common";
import type { AuthenticationStrategiesConfig } from "@jti/rest-api-types";

import type { RestResponseType } from "../../../Rest";
import { CasBaseApi } from "../../CasBaseApi";
import { Endpoints } from "../../CasEndpoints";
import type { CasAuthApiContract, ResetPasswordRequest } from "../CasAuthApi.types";

/** @deprecated Legacy proxy implementation — superseded by EVO. To be deleted after production verification. */
export class CasAuthApiLegacy extends CasBaseApi implements CasAuthApiContract {
  private Endpoints;
  constructor(
    baseUrl: string,
    protected tenantToken: string,
    protected ciamToken: string
  ) {
    super(baseUrl, tenantToken, ciamToken);
    this.Endpoints = Endpoints;
  }

  // FIXME: Old CAS validate return 200 which is used in some places, new endpoint returns 204 now, which will break logic in those places
  async validate(ciamToken: string, isCached?: boolean) {
    return this.request(
      this.Endpoints.Validate,
      { body: JSON.stringify({ token: ciamToken }), method: "POST" },
      undefined,
      isCached
    );
  }

  async login(variables: LoginVariables): Promise<RestResponseType<LoginResponse>> {
    return this.request<LoginResponse>(this.Endpoints.Login, { method: "POST", body: JSON.stringify(variables) }, true);
  }

  /**
   * Registration method
   * @param {RegisterVariables} variables
   * @returns {RegisterVariablesSuccess}
   * @throws {import("./EvoTypes").ValidationResponseError} 422 - Validation Response code
   * @throws {import("./EvoTypes").CommonResponseError} 400,409,424
   */

  async register(variables: RegisterVariables): Promise<RestResponseType<RegisterVariablesSuccess>> {
    return this.request<RegisterVariablesSuccess>(
      this.Endpoints.Register,
      {
        method: "POST",
        body: JSON.stringify(variables),
      },
      true
    );
  }

  async registerCdpUser(
    variables: RegisterCdpVariables
  ): Promise<RestResponseType<RegisterVariablesSuccess | RegisterVariablesError>> {
    return this.request("/register-cdp-user", {
      method: "POST",
      body: JSON.stringify(variables),
    });
  }

  async unregister(uuid?: string, ciamToken?: string): Promise<unknown> {
    if (uuid === undefined) {
      return Promise.reject(new Error("Consumer ID is not defined"));
    }

    const overrideHeaders = ciamToken !== undefined ? { headers: { "X-DES-CONSUMER-IAM-TOKEN": ciamToken } } : {};

    return this.request(this.replaceUuid(this.Endpoints.CancelAccount, uuid), {
      method: "PUT",
      ...overrideHeaders,
    });
  }

  async deactivateAccount(): Promise<void> {
    throw new Error("Deactivation not available for CAS");
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      this.Endpoints.ForgotPassword,
      {
        method: "POST",
        body: JSON.stringify({
          email,
        }),
      },
      true
    );
  }

  async resetConsumerPassword({
    newPassword,
    actionType,
    ciamToken,
    confirmPassword,
    consents,
  }: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    return this.request<ResetPasswordResponse>(
      this.Endpoints.ResetPassword,
      {
        method: "PUT",
        body: JSON.stringify({
          new_password: newPassword,
          token: ciamToken,
          new_password_confirmation: confirmPassword,
          action_type: actionType,
          consents,
        }),
      },
      true
    );
  }

  async verifyEmailUpdate(uuidOfRequest: string): Promise<VerifyUpdateEmailResponse> {
    return this.request(this.Endpoints.VerifyEmailUpdate, {
      body: JSON.stringify({
        uuid: uuidOfRequest,
      }),
      method: "PATCH",
    });
  }

  async verifyConsumer(email: string): Promise<{ statusCode: string }> {
    return this.request<{ statusCode: string }>(this.replaceEmail(this.Endpoints.VerifyEmail, email), {}, true);
  }

  async getPasswordRequirements(): Promise<RestResponseType<PasswordRequirementsResponse>> {
    return this.request<PasswordRequirementsResponse>(this.Endpoints.PasswordRequirements, {
      method: "GET",
    });
  }

  /** No-op: legacy CAS has no public configuration endpoint. Returns false as a default */
  async getRegistrationPublicConfiguration(): Promise<RestResponseType<{ requiresDeviceRegistration: boolean }>> {
    return {
      statusCode: 200,
      requiresDeviceRegistration: false,
    };
  }

  async dsarRequest(variables: DsarRequestPayload): Promise<RestResponseType> {
    return this.request(
      this.Endpoints.Privacy,
      {
        method: "POST",
        body: JSON.stringify(variables),
      },
      true
    );
  }

  async getAvailableAuthMethods(): Promise<RestResponseType<AuthenticationStrategiesConfig>> {
    throw new Error("Getting available auth methods is not supported in CAS");
  }
}
